// Session 38 Phase 2 · Admin Venue Edit — all 24 fields + child workspaces
import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import AdminPhotoUploader from "../components/AdminPhotoUploader";
import ExternalBookingSection from "../components/ExternalBookingSection";
import NominatimAutocomplete from "../components/NominatimAutocomplete";
import api from "../lib/api";
import { toast } from "sonner";

const FIELD_LABELS = {
  name: "Venue name", organization_id: "Organization ID",
  description: "Description", venue_type: "Venue type",
  address_line_1: "Address line 1", address_line_2: "Address line 2",
  city: "City / town", county: "County", state: "State", postal_code: "ZIP code", country: "Country",
  latitude: "Latitude", longitude: "Longitude", timezone: "Timezone",
  phone: "Phone", email: "Email", website: "Website",
  amenities: "Amenities (comma-separated)", photos: "Photo URLs (one per line)",
  policies: "Policies", parking_info: "Parking information",
  accessibility_info: "Accessibility information",
};

const VENUE_TYPES = ["Coworking Space", "Event Venue", "Meeting Room", "Private Office"];

export default function AdminVenueEdit() {
  const { venueId } = useParams();
  const nav = useNavigate();
  const [venue, setVenue] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/venues/${venueId}`);
      setVenue(r.data);
      setForm({
        name: r.data.name || "",
        organization_id: r.data.organization_id || "",
        description: r.data.description || "",
        venue_type: r.data.venue_type || "",
        address_line_1: r.data.address_line_1 || "",
        address_line_2: r.data.address_line_2 || "",
        city: r.data.city || "",
        county: r.data.county || "",
        state: r.data.state || "",
        postal_code: r.data.postal_code || "",
        country: r.data.country || "US",
        latitude: r.data.latitude ?? "",
        longitude: r.data.longitude ?? "",
        timezone: r.data.timezone || "",
        phone: r.data.phone || "",
        email: r.data.email || "",
        website: r.data.website || "",
        amenities: (r.data.amenities || []).join(", "),
        photos: (r.data.photos || []).join("\n"),
        policies: r.data.policies || "",
        parking_info: r.data.parking_info || "",
        accessibility_info: r.data.accessibility_info || "",
        published: !!r.data.published,
        featured: !!r.data.featured,
        booking_status: r.data.booking_status || "open",
        // Session 49 · External booking config (venue-level defaults)
        management_mode: r.data.management_mode || "metier_managed",
        external_booking_url: r.data.external_booking_url || "",
        booking_cta_override: r.data.booking_cta_override || "",
        cta_disclosure: r.data.cta_disclosure || "",
        open_in_new_tab: r.data.open_in_new_tab !== false,
        track_external_clicks: r.data.track_external_clicks !== false,
      });
      const r2 = await api.get(`/admin/venues/${venueId}/workspaces`);
      setWorkspaces(r2.data.workspaces || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load venue");
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.latitude === "") payload.latitude = null;
      if (payload.longitude === "") payload.longitude = null;
      payload.amenities = form.amenities
        ? form.amenities.split(",").map(s => s.trim()).filter(Boolean)
        : [];
      payload.photos = form.photos
        ? form.photos.split("\n").map(s => s.trim()).filter(Boolean)
        : [];
      await api.put(`/admin/venues/${venueId}`, payload);
      toast.success("Venue saved");
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const retryGeocode = async () => {
    try {
      const r = await api.post(`/admin/venues/${venueId}/retry-geocode`);
      if (r.data.status === "ok") {
        toast.success(`Geocoded → ${r.data.latitude?.toFixed(4)}, ${r.data.longitude?.toFixed(4)}`);
      } else {
        toast.warning(`Geocode ${r.data.status}: ${r.data.error || "no result"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  if (!venue) return <AppShell><div className="m-container" style={{padding:60}}>Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="m-container" style={{ paddingTop: 32, paddingBottom: 80 }}
           data-testid="admin-venue-edit-page">
        <div style={{ marginBottom: 4, letterSpacing: "0.18em", fontSize: 11,
                       textTransform: "uppercase", color: "rgba(27,35,64,0.55)" }}>
          <Link to="/admin/inventory/venues">Admin · Inventory · Venues</Link> ·
          Edit
        </div>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 36, margin: "6px 0 6px",
                      color: "var(--m-navy)" }}>
          {venue.name}
        </h1>
        <div style={{ display: "flex", gap: 8, fontSize: 12, color: "rgba(27,35,64,0.55)",
                       marginBottom: 24 }}>
          <span>ID: <code>{venue.venue_id}</code></span>
          <span>· {venue.slug}</span>
          <span>· org: {venue.organization?.name || venue.organization_id || "—"}</span>
        </div>

        <form onSubmit={save} style={{ display: "grid",
                                          gridTemplateColumns: "2fr 1fr", gap: 32 }}>
          <div>
            <SectionTitle>Identity</SectionTitle>
            <FieldGrid>
              <Field label={FIELD_LABELS.name} testid="venue-edit-name">
                <input value={form.name} onChange={e => set("name", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.venue_type} testid="venue-edit-venue-type">
                <select value={form.venue_type}
                         onChange={e => set("venue_type", e.target.value)}>
                  <option value="">—</option>
                  {VENUE_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label={FIELD_LABELS.organization_id} full testid="venue-edit-org-id">
                <input value={form.organization_id}
                        onChange={e => set("organization_id", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.description} full testid="venue-edit-description">
                <textarea rows={4} value={form.description}
                           onChange={e => set("description", e.target.value)} />
              </Field>
            </FieldGrid>

            <SectionTitle>Address</SectionTitle>
            <p className="m-muted small" style={{ marginTop: -8, marginBottom: 14 }}>
              Location belongs to the venue. Changes cascade automatically to every child workspace.
              Type a street address to autocomplete via OpenStreetMap.
            </p>
            <FieldGrid>
              <Field label={FIELD_LABELS.address_line_1} full testid="venue-edit-addr1">
                <NominatimAutocomplete
                  value={form.address_line_1}
                  onChange={(v) => set("address_line_1", v)}
                  testid="venue-edit-addr1-auto"
                  onPick={(picked) => {
                    setForm(f => ({
                      ...f,
                      address_line_1: picked.address_line_1 || f.address_line_1,
                      city: picked.city || f.city,
                      // Always overwrite county on OSM pick — even with "" —
                      // so a DC/federal address doesn't inherit a stale county
                      // from the prior save.
                      county: picked.county ?? f.county,
                      state: picked.state || f.state,
                      postal_code: picked.postal_code || f.postal_code,
                      country: picked.country || f.country,
                      latitude: picked.latitude ?? f.latitude,
                      longitude: picked.longitude ?? f.longitude,
                    }));
                    setDirty(true);
                    toast.success("Address filled from OpenStreetMap");
                  }}
                />
              </Field>
              <Field label={FIELD_LABELS.address_line_2} full testid="venue-edit-addr2">
                <input value={form.address_line_2}
                        onChange={e => set("address_line_2", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.city} testid="venue-edit-city">
                <input value={form.city} onChange={e => set("city", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.county} testid="venue-edit-county">
                <input value={form.county} onChange={e => set("county", e.target.value)}
                        placeholder="Middlesex" />
              </Field>
              <Field label={FIELD_LABELS.state} testid="venue-edit-state">
                <input value={form.state} onChange={e => set("state", e.target.value)}
                        placeholder="NH" />
              </Field>
              <Field label={FIELD_LABELS.postal_code} testid="venue-edit-postal">
                <input value={form.postal_code}
                        onChange={e => set("postal_code", e.target.value)}
                        placeholder="03110" />
              </Field>
              <Field label={FIELD_LABELS.country} testid="venue-edit-country">
                <input value={form.country} onChange={e => set("country", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.latitude} testid="venue-edit-lat">
                <input type="number" step="0.000001" value={form.latitude}
                        onChange={e => set("latitude", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.longitude} testid="venue-edit-lng">
                <input type="number" step="0.000001" value={form.longitude}
                        onChange={e => set("longitude", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.timezone} testid="venue-edit-tz">
                <input value={form.timezone} onChange={e => set("timezone", e.target.value)}
                        placeholder="America/New_York" />
              </Field>
            </FieldGrid>

            <SectionTitle>Contact</SectionTitle>
            <FieldGrid>
              <Field label={FIELD_LABELS.phone} testid="venue-edit-phone">
                <input value={form.phone} onChange={e => set("phone", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.email} testid="venue-edit-email">
                <input type="email" value={form.email}
                        onChange={e => set("email", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.website} full testid="venue-edit-website">
                <input value={form.website} onChange={e => set("website", e.target.value)} />
              </Field>
            </FieldGrid>

            <SectionTitle>Amenities · Photos · Policies</SectionTitle>
            <FieldGrid>
              <Field label={FIELD_LABELS.amenities} full testid="venue-edit-amenities">
                <input value={form.amenities}
                        onChange={e => set("amenities", e.target.value)}
                        placeholder="WiFi, Parking, Coffee" />
              </Field>
              <Field label={FIELD_LABELS.photos} full testid="venue-edit-photos">
                <textarea rows={3} value={form.photos}
                           onChange={e => set("photos", e.target.value)}
                           placeholder="https://…" />
                <AdminPhotoUploader
                  value={form.photos}
                  onChange={(v) => set("photos", v)}
                  testid="venue-edit"
                  previewName={form.name}
                  previewMeta={[form.city, form.state].filter(Boolean).join(", ")}
                  previewType={form.type || "Venue"}
                  previewSlug={form.slug}
                />
              </Field>
              <Field label={FIELD_LABELS.policies} full testid="venue-edit-policies">
                <textarea rows={3} value={form.policies}
                           onChange={e => set("policies", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.parking_info} full testid="venue-edit-parking">
                <textarea rows={2} value={form.parking_info}
                           onChange={e => set("parking_info", e.target.value)} />
              </Field>
              <Field label={FIELD_LABELS.accessibility_info} full testid="venue-edit-a11y">
                <textarea rows={2} value={form.accessibility_info}
                           onChange={e => set("accessibility_info", e.target.value)} />
              </Field>
            </FieldGrid>

            <SectionTitle>Status</SectionTitle>
            <div style={{ display: "flex", gap: 18, alignItems: "center",
                           padding: "12px 16px", background: "rgba(27,35,64,0.03)",
                           border: "1px solid rgba(27,35,64,0.08)", borderRadius: 8 }}>
              <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}
                      data-testid="venue-edit-published-toggle">
                <input type="checkbox" checked={!!form.published}
                        onChange={e => set("published", e.target.checked)}
                        style={{ width: 16, height: 16 }} />
                <span>Published</span>
              </label>
              <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}
                      data-testid="venue-edit-featured-toggle">
                <input type="checkbox" checked={!!form.featured}
                        onChange={e => set("featured", e.target.checked)}
                        style={{ width: 16, height: 16 }} />
                <span>Featured</span>
              </label>
              <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}
                      data-testid="venue-edit-booking-status">
                <span>Booking:</span>
                <select value={form.booking_status}
                         onChange={e => set("booking_status", e.target.value)}
                         style={{ padding: "6px 10px" }}>
                  <option value="open">open</option>
                  <option value="closed">closed</option>
                  <option value="paused">paused</option>
                </select>
              </label>
            </div>

            <ExternalBookingSection form={form} set={set} slug={form.slug} scope="venue" />

            <div style={{ marginTop: 30, display: "flex", gap: 12 }}>
              <button type="submit" className="m-btn-gold" disabled={saving}
                       data-testid="venue-edit-save">
                {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </button>
              <button type="button" onClick={() => nav("/admin/inventory/venues")}
                       className="m-btn-ghost" data-testid="venue-edit-back">
                Back to Venues
              </button>
            </div>
          </div>

          {/* Right column: geocoding + child workspaces */}
          <aside>
            <SectionTitle>Geocoding</SectionTitle>
            <div style={{ padding: "14px 16px", background: "rgba(27,35,64,0.03)",
                           border: "1px solid rgba(27,35,64,0.08)", borderRadius: 8,
                           fontSize: 13 }}
                  data-testid="venue-edit-geocode-panel">
              <div>Status: <b>{venue.geocode_status || "not attempted"}</b></div>
              {venue.latitude != null && (
                <div style={{ marginTop: 4 }}>
                  Lat: {venue.latitude?.toFixed(4)}, Lng: {venue.longitude?.toFixed(4)}
                </div>
              )}
              {venue.geocoded_at && (
                <div style={{ marginTop: 4, color: "rgba(27,35,64,0.55)", fontSize: 12 }}>
                  Last attempt: {new Date(venue.geocoded_at).toLocaleString()}
                </div>
              )}
              {venue.geocode_error && (
                <div style={{ marginTop: 6, color: "#B03030", fontSize: 12 }}>
                  {venue.geocode_error}
                </div>
              )}
              <button type="button" onClick={retryGeocode} className="m-btn-ghost"
                       style={{ marginTop: 12, fontSize: 13, padding: "6px 12px" }}
                       data-testid="venue-edit-retry-geocode">
                Retry geocoding via Nominatim
              </button>
            </div>

            <SectionTitle style={{ marginTop: 32 }}>
              Workspaces at this venue · {workspaces.length}
            </SectionTitle>
            <div data-testid="venue-edit-child-workspaces"
                 style={{ border: "1px solid rgba(27,35,64,0.10)", borderRadius: 8,
                           background: "#fff" }}>
              {workspaces.length === 0 && (
                <div style={{ padding: "20px 16px", fontSize: 13,
                               color: "rgba(27,35,64,0.55)", textAlign: "center" }}>
                  No workspaces yet.
                </div>
              )}
              {workspaces.map(w => (
                <div key={w.workspace_id}
                     data-testid={`venue-edit-child-${w.workspace_id}`}
                     style={{ padding: "12px 14px",
                               borderBottom: "1px solid rgba(27,35,64,0.06)" }}>
                  <div style={{ fontWeight: 600 }}>{w.name}</div>
                  <div style={{ fontSize: 12, color: "rgba(27,35,64,0.55)",
                                 marginTop: 2 }}>
                    {w.workspace_type} · {w.capacity ? `${w.capacity} ppl · ` : ""}
                    {w.hourly ? `$${w.hourly}/hr` : "no pricing"} ·
                    <span style={{ color: w.published ? "#146F3B" : "#8A5A00" }}>
                      {" "}{w.status}
                    </span>
                  </div>
                  <div style={{ marginTop: 6, display: "flex", gap: 10 }}>
                    <Link to={`/admin/workspaces/${w.workspace_id}/edit`}
                           style={{ fontSize: 12 }}
                           data-testid={`venue-edit-child-edit-${w.workspace_id}`}>
                      Edit →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </form>
      </div>
    </AppShell>
  );
}


function SectionTitle({ children, style }) {
  return (
    <h3 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "var(--m-navy)",
                  margin: "28px 0 12px", ...style }}>{children}</h3>
  );
}

function FieldGrid({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                        gap: "14px 18px" }}>{children}</div>;
}

function Field({ label, full, testid, children }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}
         data-testid={testid}>
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                     color: "rgba(27,35,64,0.55)", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
